import { ChangeEvent, JSX, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    FormControlLabel,
    Radio,
    RadioGroup,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { AdminMiniDialog } from '../NokMini/admin-mini/components/AdminMiniDialog';
import { StatusChip } from '../NokMini/email-finder/components/StatusChip';
import { ImportMode, ImportPlan, KeyValuePair, PairOutcome, ParsedPairs, parseKeyValueFile, planImport } from './key-value';

interface KeyValueImportDialogProps {
    open: boolean;
    existing: KeyValuePair[];
    onClose: () => void;
    onApply: (pairs: KeyValuePair[]) => void;
}

/** Each outcome on the shared chip's tones: new and updates as the email finder's new and existing. */
const OUTCOME: Record<PairOutcome, { status: string, label: string }> = {
    new: { status: 'new', label: 'New' },
    updates: { status: 'existing', label: 'Updates' },
    unchanged: { status: 'cancelled', label: 'Unchanged' },
    duplicate: { status: 'error', label: 'Duplicate' },
};

const plural = (n: number, word: string): string => n + ' ' + word + (n === 1 ? '' : 's');

/**
 * Read a two-column CSV (key, value) and show what it would do before anything changes.
 *
 * Apply only fills the editor's table; nothing is saved until the form's own Save, so the admin
 * can still look over and fix what came in. Closing discards the file and changes nothing.
 */
export const KeyValueImportDialog = ({ open, existing, onClose, onApply }: KeyValueImportDialogProps): JSX.Element => {
    const fileInput = useRef<HTMLInputElement | null>(null);
    const [fileName, setFileName] = useState<string>('');
    const [parsed, setParsed] = useState<ParsedPairs | null>(null);
    const [mode, setMode] = useState<ImportMode>('merge');

    const plan: ImportPlan | null = useMemo((): ImportPlan | null =>
        parsed === null ? null : planImport(existing, parsed.pairs, mode), [existing, parsed, mode]);

    const counts: Record<PairOutcome, number> = { new: 0, updates: 0, unchanged: 0, duplicate: 0 };
    plan?.rows.forEach((row): void => { counts[row.outcome]++; });

    function close(): void {
        setFileName('');
        setParsed(null);
        setMode('merge');
        onClose();
    }

    async function choose(event: ChangeEvent<HTMLInputElement>): Promise<void> {
        const file: File | undefined = event.target.files?.[0];
        // Cleared so choosing the same file again, after fixing it, still fires.
        event.target.value = '';
        if (file === undefined) {
            return;
        }
        setFileName(file.name);
        setParsed(await parseKeyValueFile(file));
    }

    // An empty file never applies: as a replace it would wipe the list.
    const canApply: boolean = plan !== null && plan.rows.length > 0;

    return (
        <AdminMiniDialog
            open={ open }
            onClose={ close }
            title="Import pairs from CSV"
            width="md"
            actions={
                <Button
                    variant="contained"
                    size="small"
                    disabled={ !canApply }
                    onClick={ (): void => {
                        if (plan !== null) {
                            onApply(plan.result);
                            close();
                        }
                    } }
                >
                    Apply
                </Button>
            }
        >
            <Box sx={ { pt: 1 } }>
                <Typography sx={ { fontSize: 13, color: 'text.secondary', mb: 1.5 } }>
                    Two columns: key, then value. A first row reading "key,value" is treated as a header.
                    Keys match without case, so "Foo" updates "foo". Nothing is saved until you press Save on the setting.
                </Typography>

                <Box sx={ { display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 } }>
                    <Button variant="outlined" size="small" onClick={ (): void => fileInput.current?.click() }>
                        { parsed === null ? 'Choose file' : 'Choose another file' }
                    </Button>
                    <Typography sx={ { fontSize: 13 } }>{ fileName }</Typography>
                    <input ref={ fileInput } type="file" accept=".csv,text/csv" hidden onChange={ choose } />
                </Box>

                { parsed !== null && plan !== null &&
                    <>
                        <RadioGroup
                            row
                            value={ mode }
                            onChange={ (event: ChangeEvent<HTMLInputElement>): void => setMode(event.target.value as ImportMode) }
                            sx={ { mb: 1 } }
                        >
                            <FormControlLabel value="merge" control={ <Radio size="small" /> }
                                label="Merge: update matching keys, keep the rest" />
                            <FormControlLabel value="replace" control={ <Radio size="small" /> }
                                label="Replace: the file becomes the whole list" />
                        </RadioGroup>

                        { parsed.parseErrors.map((error: string): JSX.Element =>
                            <Alert key={ error } severity="error" variant="outlined" sx={ { mb: 1, fontSize: 13 } }>{ error }</Alert>) }

                        <Alert severity={ counts.duplicate > 0 ? 'warning' : 'info' } variant="outlined" sx={ { mb: 2, fontSize: 13 } }>
                            { [
                                plural(plan.rows.length, 'row') + ' read',
                                mode === 'merge' ? counts.new + ' new' : null,
                                mode === 'merge' ? (counts.updates === 1 ? '1 updates an existing key' : counts.updates + ' update existing keys') : null,
                                mode === 'merge' && counts.unchanged > 0 ? counts.unchanged + ' unchanged' : null,
                                plan.removed > 0 ? plural(plan.removed, 'existing pair') + ' removed' : null,
                                counts.duplicate > 0 ? plural(counts.duplicate, 'repeated key') + ' in the file' : null,
                                parsed.skippedRows > 0 ? plural(parsed.skippedRows, 'row') + ' with no key skipped' : null,
                                parsed.extraCellRows > 0 ? plural(parsed.extraCellRows, 'row') + ' had extra columns, ignored' : null,
                            ].filter((part: string | null): boolean => part !== null).join(', ') + '.' }
                            { counts.duplicate > 0 &&
                                ' Repeated keys are kept as their own rows and flagged in the editor; Save stays off until they are fixed.' }
                        </Alert>

                        { plan.rows.length > 0 &&
                            <Table size="small" sx={ { tableLayout: 'fixed', '& td, & th': { borderColor: 'divider', fontSize: 13 } } }>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={ { width: 120 } }>Result</TableCell>
                                        <TableCell sx={ { width: '35%' } }>Key</TableCell>
                                        <TableCell>Value</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    { plan.rows.map((row, index: number): JSX.Element => {
                                        const outcome: PairOutcome = row.outcome;
                                        return (
                                            <TableRow key={ index }>
                                                <TableCell>
                                                    <StatusChip status={ OUTCOME[outcome].status } label={ OUTCOME[outcome].label } dense />
                                                </TableCell>
                                                <TableCell sx={ { overflowWrap: 'anywhere' } }>{ row.pair.key }</TableCell>
                                                <TableCell sx={ { overflowWrap: 'anywhere' } }>
                                                    { row.outcome === 'updates' &&
                                                        <Typography component="span" sx={ { fontSize: 13, color: 'text.secondary', textDecoration: 'line-through', mr: 1 } }>
                                                            { row.previous }
                                                        </Typography>
                                                    }
                                                    { row.pair.value }
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }) }
                                </TableBody>
                            </Table>
                        }
                    </>
                }
            </Box>
        </AdminMiniDialog>
    );
};
